import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ErrorState } from '@/components/ErrorState';
import { cn } from '@/lib/cn';
import { LoadingRegion, Skeleton } from '@/components/Skeleton';
import { useHelpRequests, useLocations, useManageLive, useManageMenu } from './queries';
import { helpLabel, locationText, waitedText } from './wording';
import { PageHeader } from '@/components/PageHeader';
import { StatTile } from '@/components/StatTile';
import { cardClasses } from '@/components/Card';
import { SectionHeading } from '@/components/SectionHeading';

function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/** Column count by tile count, so the grid is always full: 5 tiles in 5 columns, 6 in 3, 4 in 4. */
const COLUMNS: Record<number, string> = {
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-3',
};

/**
 * What is happening in the branch right now, from reads the backend already offers. Every number is a count
 * of a list the server sent. No sales, revenue or order statistics: no endpoint supplies them. The branch
 * screens are linked from the rail (and the phone strip) only, not again on the page.
 */
export function OverviewPage() {
  useManageLive();
  const locations = useLocations();
  const help = useHelpRequests();
  const menu = useManageMenu();
  const now = useNow();

  const perType = useMemo(() => {
    const counts = new Map<string, { total: number; inUse: number }>();
    for (const l of locations.data ?? []) {
      const entry = counts.get(l.location_type) ?? { total: 0, inUse: 0 };
      entry.total += 1;
      if (l.status === 'OCCUPIED' || l.active_sessions.length > 0) entry.inUse += 1;
      counts.set(l.location_type, entry);
    }
    return [...counts.entries()];
  }, [locations.data]);

  const sessions = (locations.data ?? []).flatMap((l) => l.active_sessions);
  const waitingOnKitchen = sessions.filter((s) => s.has_pending_orders).length;
  const unavailable = (menu.data?.categories ?? [])
    .flatMap((c) => c.items)
    .filter((i) => !i.is_available);
  const pendingHelp = (help.data ?? []).filter((r) => r.status === 'PENDING');

  const reads = [locations, help, menu];
  const refetchAll = () => {
    void locations.refetch();
    void help.refetch();
    void menu.refetch();
  };
  // One loading state while nothing has arrived; one error block for any read that failed with no data.
  const loading = reads.some((q) => q.isPending) && reads.every((q) => q.data === undefined);
  const failed = reads.find((q) => q.isError && q.data === undefined);

  /** The tiles that have data: a count of a list the server sent, each under its own label. */
  const tiles: { label: string; value: string | number }[] = [];
  if (locations.data) {
    if (perType.length === 0) tiles.push({ label: 'Locations', value: 'None set up' });
    for (const [type, c] of perType) {
      tiles.push({
        label:
          type === 'SEAT'
            ? 'Seats in use'
            : type === 'TABLE'
              ? 'Tables in use'
              : 'Locations in use',
        value: `${c.inUse} of ${c.total}`,
      });
    }
    tiles.push({ label: 'Open sessions', value: sessions.length });
    tiles.push({ label: 'Waiting on the kitchen', value: waitingOnKitchen });
  }
  if (help.data) tiles.push({ label: 'Help requests waiting', value: pendingHelp.length });
  if (menu.data) tiles.push({ label: 'Unavailable menu items', value: unavailable.length });

  return (
    <section aria-labelledby="overview-title" className="flex flex-col gap-4">
      <PageHeader
        id="overview-title"
        title="Overview"
        actions={
          <Button
            variant="secondary"
            loading={reads.some((q) => q.isFetching) && !loading}
            onClick={refetchAll}
          >
            Refresh
          </Button>
        }
      />

      {loading && (
        <LoadingRegion slowHint label="Loading the overview">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            {[0, 1, 2, 3, 4].map((n) => (
              <Skeleton key={n} className="h-24" />
            ))}
          </div>
        </LoadingRegion>
      )}

      {failed && <ErrorState error={failed.error} onRetry={refetchAll} />}

      {tiles.length > 0 && (
        <section aria-labelledby="now-title" className="flex flex-col gap-2">
          <SectionHeading id="now-title">Right now</SectionHeading>
          <dl
            className={cn(
              'grid grid-cols-2 gap-3',
              COLUMNS[tiles.length] ?? 'lg:grid-cols-3',
              // An odd tile count on a phone: the last tile takes the whole row, so the grid has no hole.
              tiles.length % 2 === 1 &&
                '[&>*:last-child]:col-span-2 lg:[&>*:last-child]:col-span-1',
            )}
          >
            {tiles.map((tile) => (
              <StatTile key={tile.label} label={tile.label} value={tile.value} />
            ))}
          </dl>
        </section>
      )}

      {pendingHelp.length > 0 && (
        <section aria-labelledby="help-title" className="flex flex-col gap-2">
          <SectionHeading
            id="help-title"
            action={
              <Link
                to="/staff/requests"
                className="inline-flex min-h-10 items-center text-sm font-semibold text-primary"
              >
                See requests
              </Link>
            }
          >
            Help requests
          </SectionHeading>
          <ul
            className={cn(cardClasses({ padding: 'none' }), 'flex flex-col divide-y divide-line')}
          >
            {[...pendingHelp]
              .sort((a, b) => a.created_at.localeCompare(b.created_at))
              .map((r) => (
                <li
                  key={r.request_id}
                  className="relative flex flex-wrap items-center justify-between gap-2 py-2.5 pr-4 pl-4 text-sm"
                >
                  <span
                    aria-hidden="true"
                    className="absolute inset-y-0 left-0 w-1.5 bg-attention"
                  />
                  <span className="font-semibold text-ink">
                    {locationText(r.location_type, r.location_label)}
                  </span>
                  <span className="text-muted">
                    {helpLabel(r.type)} · {waitedText(r.created_at, now)}
                  </span>
                </li>
              ))}
          </ul>
        </section>
      )}

      {unavailable.length > 0 && (
        <section aria-labelledby="off-title" className="flex flex-col gap-2">
          <SectionHeading
            id="off-title"
            action={
              <Link
                to="/manage/menu"
                className="inline-flex min-h-10 items-center text-sm font-semibold text-primary"
              >
                See menu
              </Link>
            }
          >
            Unavailable items
          </SectionHeading>
          <ul
            className={cn(cardClasses({ padding: 'none' }), 'flex flex-col divide-y divide-line')}
          >
            {unavailable.map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm break-words text-ink"
              >
                {item.name}
                <Badge tone="neutral">Unavailable</Badge>
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  );
}
